import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button.jsx'
import { TypographySmall } from '@/components/ui/typography.jsx'
import { Icon } from '@iconify/react'
import { initialChat, chat, signUrlUploadCreativeAsset, getChatStatus, getAiInstructions } from '@/services/api.js'
import { toast } from 'sonner'
import { AnalyticsService } from '@/services/analytics.js'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
    Conversation,
    ConversationContent,
    ConversationEmptyState
} from '@/components/ai-elements/conversation'
import {
    Message,
    MessageContent,
    MessageActions,
    MessageAction
} from '@/components/ai-elements/message'
import {
    PromptInput,
    PromptInputTextarea,
    PromptInputFooter,
    PromptInputTools,
    PromptInputSubmit,
    PromptInputButton,
    PromptInputAttachments,
    PromptInputAttachment
} from '@/components/ai-elements/prompt-input'
import { Suggestions, Suggestion } from '@/components/ai-elements/suggestion'
import { Reasoning, ReasoningTrigger } from '@/components/ai-elements/reasoning'
import { MarkdownTypewriter } from '@/components/typewriter/HtmlTypewriter.jsx'
import { Streamdown } from 'streamdown'
import { BlocksContainer } from '@/components/ai-elements/ui-blocks'
import { useDashboardContext } from '@/context-state/DashboardContext'
import { useCreativeAiChat } from '@/context-state/CreativeAiChatContext.jsx'
import { usePaymentContext, readDevFlag } from '@/context-state/PaymentContext.jsx'
import { toggleAppBanner } from '@/hooks/useAppBanner.jsx'
import { Shimmer } from '@/components/ai-elements/shimmer'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu.jsx'
import { FutureLoader } from '@/components/loader/FutureLoader'
import { AdAccountSelect } from '@/components/ad-account-select/AdAccountSelect.jsx'

// Encapsulates the rendering of an assistant's message, including the typewriter effect.
// This is done to ensure that hooks like `useCallback` are called at the top level.
const AssistantMessage = ({ message, setCompletedMessages, completedMessages }) => {
    const handleComplete = useCallback(() => {
        setCompletedMessages(prev => {
            // Avoids re-setting state if the ID is already there
            if (prev.has(message.id)) {
                return prev
            }
            return new Set(prev).add(message.id)
        })
    }, [message.id, setCompletedMessages])

    const handleCopy = useCallback(async () => {
        try {
            // Remove markdown formatting for plain text copy
            const textToCopy = message.content || '' // ?.replace(/[*_#`~\[\]()]/g, '').replace(/\n{2,}/g, '\n').trim() || ''
            await navigator.clipboard.writeText(textToCopy)
            toast.success('Copied to clipboard')
            AnalyticsService.track('chat-message-copied', { messageId: message.id })
        } catch (err) {
            console.error('Failed to copy text:', err)
            toast.error('Failed to copy message')
        }
    }, [message.content, message.id])

    const handleThumbsUp = useCallback(() => {
        AnalyticsService.track('chat-message-thumbs-up', { messageId: message.id })
        toast.success('Feedback recorded')
    }, [message.id])

    const handleThumbsDown = useCallback(() => {
        AnalyticsService.track('chat-message-thumbs-down', { messageId: message.id })
        toast.success('Feedback recorded')
    }, [message.id])

    // If this message was already completed before mount (loaded from history), skip the typewriter
    const isPreCompleted = completedMessages.has(message.id)

    // Mark as completed immediately if pre-completed so action buttons and uiPayload show
    useEffect(() => {
        if (isPreCompleted) {
            handleComplete()
        }
    }, [])

    const actionButtons = (
        <motion.div
            initial={isPreCompleted ? false : { opacity: 0, y: 10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="absolute -bottom-1 -left-1"
        >
            <MessageActions className="relative flex items-center gap-1 p-1">
                <MessageAction
                    tooltip="Copy message"
                    label="Copy"
                    onClick={handleCopy}
                    size="icon-sm"
                    className="bg-background rounded-md border shadow-sm p-1.5 transition-colors hover:bg-accent"
                >
                    <Icon icon="ph:copy" className="h-3.5 w-3.5" />
                </MessageAction>
                <MessageAction
                    tooltip="Helpful"
                    label="Thumbs up"
                    onClick={handleThumbsUp}
                    size="icon-sm"
                    className="bg-background rounded-md border shadow-sm p-1.5 transition-colors hover:bg-accent"
                >
                    <Icon icon="ph:thumbs-up" className="h-3.5 w-3.5" />
                </MessageAction>
                <MessageAction
                    tooltip="Not helpful"
                    label="Thumbs down"
                    onClick={handleThumbsDown}
                    size="icon-sm"
                    className="bg-background rounded-md border shadow-sm p-1.5 transition-colors hover:bg-accent"
                >
                    <Icon icon="ph:thumbs-down" className="h-3.5 w-3.5" />
                </MessageAction>
            </MessageActions>
        </motion.div>
    )

    return (
        <div className="space-y-4">
            {message.content && (
                <div className="relative pb-8">
                    {isPreCompleted ? (
                        <div className="leading-6 text-sm relative">
                            <Streamdown
                                allowedImagePrefixes={['https://storage.googleapis.com']}
                                defaultOrigin="https://storage.googleapis.com"
                                components={{
                                    img: ({ src, alt }) => <img src={src} alt={alt} className="max-w-48 rounded-lg object-cover" />
                                }}
                            >
                                {message.content}
                            </Streamdown>
                        </div>
                    ) : (
                        <MarkdownTypewriter
                            markdown={message.content}
                            speed={10}
                            onComplete={handleComplete}
                        />
                    )}
                    {completedMessages.has(message.id) && actionButtons}
                </div>
            )}
            {/* Render UI blocks only after the typewriter has finished */}
            {completedMessages.has(message.id) && message.uiPayload?.length > 0 && (
                <BlocksContainer blocks={message.uiPayload} />
            )}
        </div>
    )
}

const REASONING_MESSAGES = [
    "Analyzing your request and gathering relevant information...",
    "Searching through your creative library and performance data...",
    "Examining ad metrics and engagement patterns...",
    "Reviewing creative performance across different ads...",
    "Identifying key insights and trends in your creative data...",
    "Comparing creative performance with historical benchmarks...",
    "Analyzing patterns and trends in your creative data...",
    "Processing creative elements and their impact on performance...",
    "Analyzing the impact of creative elements on performance...",
    "Generating insights and recommendations...",
]

const SUGGESTION_MESSAGES = [
    "Analyze my ad creative before I publish it",
    "What insights can you provide about my creatives?",
    "Give me new script ideas for my next ad",
    "Give me new angle ideas for my next ads",
    "Which creative angles should i do more?",
    "Which creative angles am i missing?",
    "Give me recommendations for improving my ad strategy",
    "What's my top performing ads from last month?",
    "Let's explore more target personas I could address",
    "What missing opportunities do you see in my ad strategy?",
    "Show me which creatives have the best ROAS",
    "What creative elements drive the most engagement?",
    "What are the common traits of my winning ads?",
    "Suggest improvements for my lowest performing creatives"
]

// Simple typewriter component for reasoning text
const TypewriterText = ({ text, speed = 20 }) => {
    const [displayedText, setDisplayedText] = useState('')
    const [currentIndex, setCurrentIndex] = useState(0)

    useEffect(() => {
        setDisplayedText('')
        setCurrentIndex(0)
    }, [text])

    useEffect(() => {
        if (currentIndex < text.length) {
            const timeout = setTimeout(() => {
                setDisplayedText(prev => prev + text[currentIndex])
                setCurrentIndex(prev => prev + 1)
            }, speed)
            return () => clearTimeout(timeout)
        }
    }, [currentIndex, text, speed])

    return (
        // <Shimmer duration={1.4}>
        <span>
            {displayedText}
        </span>
        // </Shimmer>
    )
}

export default function ChatPage({
    externalChatId = null,
    externalMessages = null,
    externalSetMessages = null,
    externalIsFirstMessage = null,
    externalSetIsFirstMessage = null,
    externalSetChatId = null,
    externalClearChat = null,
    externalIsLoading = null,
    externalSetIsLoading = null,
    externalIsTyping = null,
    externalSetIsTyping = null,
    externalPendingQuestion = null,
    externalSetPendingQuestion = null,
    externalCreativeContext = null,
    externalSetCreativeContext = null,
    initialCompletedMessageIds = null,
    chatSource = null,
    onChatCreated = null
}) {
    const dashboardContext = useDashboardContext()
    const chosenAdAccount = dashboardContext?.adAccountsContext?.chosenAdAccount
    const {
        closeDrawer,
        getChatStatus: getChatStatusFromContext,
        triggerStatusCheck,
        isCheckingStatus: isCheckingStatusFromContext,
        selectedInstructionId,
        setSelectedInstructionId,
        advertiserContext,
    } = useCreativeAiChat()

    // Get current chat status from context
    const chatStatus = chosenAdAccount ? getChatStatusFromContext(chosenAdAccount) : null
    const isCheckingStatus = isCheckingStatusFromContext
    const { accountId, spaceId } = useParams()

    // Use external state if provided, otherwise use internal state
    const [internalMessages, internalSetMessages] = useState([])
    const [internalChatId, internalSetChatId] = useState(null)
    const [internalIsFirstMessage, internalSetIsFirstMessage] = useState(true)
    const [internalIsLoading, internalSetIsLoading] = useState(false)
    const [internalIsTyping, internalSetIsTyping] = useState(false)

    const messages = externalMessages !== null ? externalMessages : internalMessages
    const setMessages = externalSetMessages || internalSetMessages
    const chatId = externalChatId !== null ? externalChatId : internalChatId
    const setChatId = externalSetChatId || internalSetChatId
    const isFirstMessage = externalIsFirstMessage !== null ? externalIsFirstMessage : internalIsFirstMessage
    const setIsFirstMessage = externalSetIsFirstMessage || internalSetIsFirstMessage
    const isLoading = externalIsLoading !== null ? externalIsLoading : internalIsLoading
    const setIsLoading = externalSetIsLoading || internalSetIsLoading
    const isTyping = externalIsTyping !== null ? externalIsTyping : internalIsTyping
    const setIsTyping = externalSetIsTyping || internalSetIsTyping

    const [input, setInput] = useState('')
    const [currentReasoningIndex, setCurrentReasoningIndex] = useState(0)
    const [completedMessages, setCompletedMessages] = useState(() => initialCompletedMessageIds ? new Set(initialCompletedMessageIds) : new Set())
    const navigate = useNavigate()

    // Chat runs on the same credit meter. Free (hard-capped) is blocked once it's
    // out of credits; paying tiers are never blocked here (soft-warn, per policy).
    const { entitlements } = usePaymentContext()
    const creditsEnt = entitlements?.api_credits
    // DEV-only: localStorage.setItem('force_credit_block','1') to test the block.
    const isChatCreditBlocked = (import.meta.env.DEV && readDevFlag('force_credit_block'))
        || !!(creditsEnt?.enabled && creditsEnt?.limitReached && creditsEnt?.hardCap)

    const [instructions, setInstructions] = useState([])
    const [isInstructionMenuOpen, setIsInstructionMenuOpen] = useState(false)
    const instructionMenuRef = useRef(null)

    useEffect(() => {
        const fetchInstructions = async () => {
            try {
                const response = await getAiInstructions()
                setInstructions(response.data || [])
            } catch (error) {
                console.error('Failed to fetch instructions:', error)
            }
        }
        fetchInstructions()
    }, [])

    useEffect(() => {
        if (!isInstructionMenuOpen) {
            return
        }
        const handleClickOutside = (e) => {
            if (instructionMenuRef.current && !instructionMenuRef.current.contains(e.target)) {
                setIsInstructionMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isInstructionMenuOpen])

    // Get 3 random suggestions
    const [randomSuggestions] = useState(() => {
        const first = SUGGESTION_MESSAGES[0]
        const remaining = SUGGESTION_MESSAGES.slice(1)
        const shuffled = [...remaining].sort(() => 0.5 - Math.random())
        return [first, ...shuffled.slice(0, 2)]
    })

    // Helper to ensure chat is ready before sending anything
    const ensureChatReady = useCallback(async () => {
        if (!chosenAdAccount) {
            toast.error('No ad account selected.')
            return false
        }

        // If we're currently checking status in context, block sends
        if (isCheckingStatus) {
            toast.error('Checking chat status. Please wait a moment.')
            return false
        }

        // Fast paths when we already know the status
        if (chatStatus === 'idle' || chatStatus === 'missing-context') {
            return true
        }

        if (chatStatus === 'preparing') {
            toast.error('We’re analyzing your data. Chat will be ready soon.')
            return false
        }

        if (chatStatus === 'not-ready') {
            toast.error('Chat is not ready yet. Please check back soon.')
            return false
        }

        // Status not known yet – actively check it now via context
        const status = await triggerStatusCheck(chosenAdAccount)

        if (status === 'idle' || status === 'missing-context') {
            return true
        }

        if (status === 'preparing') {
            toast.error('We’re analyzing your data. Chat will be ready soon.')
            return false
        }

        if (status === 'not-ready') {
            toast.error('Chat is not ready yet. Please check back soon.')
            return false
        }

        // Fallback for unexpected / null status
        toast.error('Unable to start chat right now. Please try again soon.')
        return false
    }, [chatStatus, isCheckingStatus, chosenAdAccount, triggerStatusCheck])

    useEffect(() => {
        AnalyticsService.track('chat-page-viewed')
        AnalyticsService.page('chat-page')
    }, [])

    // Trigger status check when chat opens for not-ready or missing-context cases
    useEffect(() => {
        if (chosenAdAccount && triggerStatusCheck) {
            const currentStatus = chatStatus
            // Re-check status when user opens chat if status is not-ready or missing-context
            if (currentStatus === 'not-ready' || currentStatus === 'missing-context') {
                triggerStatusCheck(chosenAdAccount)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chosenAdAccount]) // Only trigger when chat opens (chosenAdAccount changes)

    // Auto-submit pending question from CreativeAskAi
    useEffect(() => {
        if (externalPendingQuestion && !isLoading) {
            // Check if there's a creative image to attach
            const hasCreativeImage = externalCreativeContext?.creativeEntity?.imageUrl

            if (hasCreativeImage && !hasCreativeImage?.endsWith('.mp4')) {
                // Submit with image attachment
                const imageAttachment = {
                    id: Date.now(),
                    type: 'file',
                    filename: 'creative-image.jpg',
                    mediaType: 'image/jpeg',
                    url: externalCreativeContext.creativeEntity.imageUrl
                }
                handleSubmitWithCreative(externalPendingQuestion, [imageAttachment], externalCreativeContext.creativeEntity._id)
            } else {
                // Auto-submit the question without attachment
                handleSuggestionClick(externalPendingQuestion)
            }

            // Clear the pending question
            if (externalSetPendingQuestion) {
                externalSetPendingQuestion(null)
            }

            // Log creative context if available
            if (externalCreativeContext) {
                AnalyticsService.track('chat-with-creative-context', {
                    creativeId: externalCreativeContext.creativeId,
                    question: externalPendingQuestion
                })
            }
        }
    }, [externalPendingQuestion])

    // Effect to set isTyping to false once the last message is fully rendered
    useEffect(() => {
        if (!messages.length || isTyping === false) return

        const lastMessage = messages[messages.length - 1]

        // Check if the last message is from the assistant and has completed its animation
        if (lastMessage.role === 'assistant' && completedMessages.has(lastMessage.id)) {
            setIsTyping(false)
        }
    }, [messages, completedMessages, isTyping, setIsTyping])


    // Cycle through reasoning messages while loading
    useEffect(() => {
        if (!isLoading) {
            setCurrentReasoningIndex(0)
            return
        }

        const interval = setInterval(() => {
            setCurrentReasoningIndex(Math.floor(Math.random() * REASONING_MESSAGES.length))
        }, 8000) // Change message every 8 seconds

        return () => clearInterval(interval)
    }, [isLoading])

    const handleClearChat = () => {
        if (externalClearChat) {
            externalClearChat()
        } else {
            setMessages([])
            setChatId(null)
            setIsFirstMessage(true)
        }
        setInput('')
        setCompletedMessages(new Set())
        setSelectedInstructionId(null)
        AnalyticsService.track('chat-cleared', { chatId })
    }

    const handleSubmitWithCreative = async (text, attachments, creativeId) => {
        if (isChatCreditBlocked) {
            toggleAppBanner('free-chat-limit', 'chat')
            return
        }

        const isReady = await ensureChatReady()
        if (!isReady) {
            return
        }

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: text,
            timestamp: new Date(),
            attachments
        }

        setMessages(prev => [...prev, userMessage])
        setInput('')
        setIsLoading(true)

        try {
            const requestData = {
                prompt: text,
                creativeId,
                adAccount: chosenAdAccount
            }
            if (selectedInstructionId) {
                requestData.instructionId = selectedInstructionId
            }
            // Include the creative image URL so it gets persisted for chat history
            if (attachments && attachments.length > 0) {
                requestData.attachments = attachments.map(a => ({
                    url: a.url,
                    filename: a.filename,
                    mediaType: a.mediaType
                }))
            }

            if (chatSource) {
                requestData.source = chatSource
            }

            const response = await initialChat(requestData)
            const { reply, chatId: newChatId, uiPayload } = response.data
            setChatId(newChatId)
            setIsFirstMessage(false)

            const aiMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: reply,
                uiPayload: uiPayload || null,
                timestamp: new Date()
            }
            setMessages(prev => [...prev, aiMessage])
            setIsTyping(true)

            if (onChatCreated) {
                onChatCreated(newChatId)
            }

            AnalyticsService.track('chat-initialized-with-creative', {
                chatId: newChatId,
                message: text,
                creativeId
            })
        } catch (error) {
            console.error('Error sending message with creative:', error)
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: 'Sorry, I encountered an error processing your message. Please try again.',
                timestamp: new Date()
            }
            setMessages(prev => [...prev, errorMessage])
            toast.error(error.response?.data?.message || 'Sorry, I encountered an error processing your message. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleSuggestionClick = async (suggestion) => {
        const isReady = await ensureChatReady()
        if (!isReady) {
            return
        }

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: suggestion,
            timestamp: new Date()
        }

        setMessages(prev => [...prev, userMessage])
        setIsLoading(true)

        try {
            let response

            if (isFirstMessage) {
                const suggestionPayload = { prompt: suggestion, adAccount: chosenAdAccount }
                if (selectedInstructionId) {
                    suggestionPayload.instructionId = selectedInstructionId
                }
                if (chatSource) {
                    suggestionPayload.source = chatSource
                }
                // Brand-page asks carry the advertiser so the AI knows which
                // brand the conversation is about without name resolution
                if (advertiserContext) {
                    suggestionPayload.advertiserContext = advertiserContext
                }
                response = await initialChat(suggestionPayload)
                const { reply, chatId: newChatId, uiPayload } = response.data
                setChatId(newChatId)
                setIsFirstMessage(false)

                const aiMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: reply,
                    uiPayload: uiPayload || null,
                    timestamp: new Date()
                }
                setMessages(prev => [...prev, aiMessage])
                setIsTyping(true)

                if (onChatCreated) {
                    onChatCreated(newChatId)
                }

                AnalyticsService.track('chat-initialized', { chatId: newChatId, message: suggestion })
            } else {
                response = await chat({
                    chatId,
                    prompt: suggestion,
                    adAccount: chosenAdAccount
                })
                const { reply, uiPayload } = response.data

                const aiMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: reply,
                    uiPayload: uiPayload || null,
                    timestamp: new Date()
                }
                setMessages(prev => [...prev, aiMessage])
                setIsTyping(true)

                AnalyticsService.track('chat-suggestion-clicked', { suggestion, chatId })
            }

        } catch (error) {
            console.error('Error sending suggestion:', error)
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: 'Sorry, I encountered an error processing your request. Please try again.',
                timestamp: new Date()
            }
            setMessages(prev => [...prev, errorMessage])
            toast.error(error.response?.data?.message || 'Sorry, I encountered an error processing your request. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleSubmit = async ({ text, files }) => {
        if ((!text.trim() && files.length === 0) || isLoading) {
            return
        }

        if (isChatCreditBlocked) {
            toggleAppBanner('free-chat-limit', 'chat')
            return
        }

        const isReady = await ensureChatReady()
        if (!isReady) {
            return
        }

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: text || 'Uploaded files',
            timestamp: new Date(),
            attachments: files
        }

        setMessages(prev => [...prev, userMessage])
        setInput('')
        setIsLoading(true)

        try {
            let fileData = null

            // Upload files if any
            if (files && files.length > 0) {
                const uploadedFiles = []

                for (const fileAttachment of files) {
                    try {
                        // Convert data URL or blob URL back to Blob
                        let blob
                        if (fileAttachment.url.startsWith('data:')) {
                            // Convert data URL to blob
                            const response = await fetch(fileAttachment.url)
                            blob = await response.blob()
                        } else if (fileAttachment.url.startsWith('blob:')) {
                            // Convert blob URL to blob
                            const response = await fetch(fileAttachment.url)
                            blob = await response.blob()
                        } else {
                            console.error('Unsupported file URL format:', fileAttachment.url)
                            toast.error(`Unable to upload ${fileAttachment.filename}`)
                            continue
                        }

                        // Check file size (100MB limit)
                        if (blob.size > 100 * 1024 * 1024) {
                            toast.error(`File ${fileAttachment.filename} is too large. Maximum size is 100MB`)
                            continue
                        }

                        // Get signed URL for upload
                        const signUrlResponse = await signUrlUploadCreativeAsset({
                            fileName: fileAttachment.filename,
                            contentType: fileAttachment.mediaType
                        })
                        const { uploadUrl, publicUrl, contentType } = signUrlResponse.data

                        // Upload blob to signed URL
                        await fetch(uploadUrl, {
                            method: 'PUT',
                            body: blob,
                            headers: {
                                'Content-Type': fileAttachment.mediaType
                            }
                        })

                        uploadedFiles.push({
                            fileUri: publicUrl,
                            mimeType: contentType
                        })
                    } catch (error) {
                        console.error('Error uploading file:', fileAttachment.filename, error)
                        toast.error(`Failed to upload ${fileAttachment.filename}`)
                    }
                }

                // Set fileData to the array of uploaded files, or single file if only one
                if (uploadedFiles.length > 0) {
                    fileData = uploadedFiles.length === 1 ? uploadedFiles[0] : uploadedFiles
                }
            }

            let response

            if (isFirstMessage) {
                const requestData = { prompt: text, adAccount: chosenAdAccount }
                if (fileData) {
                    requestData.fileData = fileData
                }
                if (selectedInstructionId) {
                    requestData.instructionId = selectedInstructionId
                }
                if (chatSource) {
                    requestData.source = chatSource
                }
                // Include attachment metadata for chat history display
                if (files && files.length > 0) {
                    requestData.attachments = files.map(a => ({
                        url: a.url,
                        filename: a.filename,
                        mediaType: a.mediaType
                    }))
                }
                if (advertiserContext) {
                    requestData.advertiserContext = advertiserContext
                }

                response = await initialChat(requestData)
                const { reply, chatId: newChatId, uiPayload } = response.data
                setChatId(newChatId)
                setIsFirstMessage(false)

                const aiMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: reply,
                    uiPayload: uiPayload || null,
                    timestamp: new Date()
                }
                setMessages(prev => [...prev, aiMessage])
                setIsTyping(true)

                if (onChatCreated) {
                    onChatCreated(newChatId)
                }

                AnalyticsService.track('chat-initialized', { chatId: newChatId, message: text })
            } else {
                const requestData = {
                    chatId,
                    prompt: text,
                    adAccount: chosenAdAccount
                }
                if (fileData) {
                    requestData.fileData = fileData
                }
                // Include attachment metadata for chat history display
                if (files && files.length > 0) {
                    requestData.attachments = files.map(a => ({
                        url: a.url,
                        filename: a.filename,
                        mediaType: a.mediaType
                    }))
                }

                response = await chat(requestData)
                const { reply, uiPayload } = response.data

                const aiMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: reply,
                    uiPayload: uiPayload || null,
                    timestamp: new Date()
                }
                setMessages(prev => [...prev, aiMessage])
                setIsTyping(true)

                AnalyticsService.track('chat-message-sent', { chatId, message: text })
            }

        } catch (error) {
            console.error('Error sending message:', error)
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: 'Sorry, I encountered an error processing your message. Please try again.',
                timestamp: new Date()
            }
            setMessages(prev => [...prev, errorMessage])
            toast.error(error.response?.data?.message || 'Sorry, I encountered an error processing your message. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        // <div className="h-full w-full flex flex-col relative overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 bg-white">
        <div className="h-full w-full flex flex-col relative overflow-hidden">
            {/* Animated liquid glass orbs background - we removed the animation.div for a reason! seems to make the broswer slower */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute w-96 h-96 rounded-full bg-gradient-to-br from-blue-200/40 to-purple-200/40 blur-3xl"
                    animate={{
                        x: [0, 100, 0],
                        y: [0, -50, 0],
                        scale: [1, 1.2, 1],
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                    style={{ top: '10%', left: '10%' }}
                />
                <div
                    className="absolute w-80 h-80 rounded-full bg-gradient-to-br from-pink-200/40 to-orange-200/40 blur-3xl"
                    animate={{
                        x: [0, -80, 0],
                        y: [0, 80, 0],
                        scale: [1, 1.3, 1],
                    }}
                    transition={{
                        duration: 18,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1
                    }}
                    style={{ top: '60%', right: '10%' }}
                />
                <div
                    className="absolute w-72 h-72 rounded-full bg-gradient-to-br from-purple-200/40 to-blue-200/40 blur-3xl"
                    animate={{
                        x: [0, 60, 0],
                        y: [0, -60, 0],
                        scale: [1, 1.1, 1],
                    }}
                    transition={{
                        duration: 22,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 2
                    }}
                    style={{ bottom: '15%', left: '30%' }}
                />
            </div>



            {/* Chat Header with Clear Button */}
            {/* Chat Header — only shown in drawer mode */}
            {externalClearChat && (
                <div className="flex items-center justify-between gap-2 p-4 border-b min-h-[56px]">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => closeDrawer()}
                        className="text-muted-foreground hover:text-foreground shrink-0"
                    >
                        <Icon icon="ph:x" className="h-4 w-4 mr-2" />
                        Close chat
                    </Button>
                    <div className="flex items-center gap-1 shrink-0">
                        {/* Ad account selector — chat operates on a single
                            account (the global chosenAdAccount). Compact, sits
                            next to the dots menu. isOpeningInsideModal renders
                            the popover without a portal so it's not clipped by
                            the drawer's pointer-events region. */}
                        <div className="w-44">
                            <AdAccountSelect isOpeningInsideModal />
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <Icon icon="mdi:dots-horizontal" className="h-5 w-5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuItem onClick={() => {
                                    if (externalClearChat) {
                                        externalClearChat()
                                    }
                                    setInput('')
                                    setCompletedMessages(new Set())
                                    setSelectedInstructionId(null)
                                    AnalyticsService.track('drawer-new-chat-clicked')
                                }}>
                                    <Icon icon="ph:plus" className="h-4 w-4 mr-2" />
                                    New Chat
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                    AnalyticsService.track('drawer-view-history-clicked')
                                    closeDrawer()
                                    navigate(`/account/${accountId}/space/${spaceId}/dashboard/chat`)
                                }}>
                                    <Icon icon="ph:clock-counter-clockwise" className="h-4 w-4 mr-2" />
                                    View History
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            )}

            {/* Status Banner for missing-context */}
            {chatStatus === 'missing-context' && (
                <Alert className="m-4 border-yellow-500/50 bg-yellow-500/10 flex">

                    <AlertDescription className="flex items-center gap-2">
                        <Icon icon="ph:warning" className="h-5 w-5 text-yellow-600" />
                        Missing relevant context. Go to AI context page to configure.
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                closeDrawer()
                                navigate(`/account/${accountId}/space/${spaceId}/dashboard/settings/space/ai-context`)
                            }}
                            className="ml-4 gap-1"
                        >

                            Configure
                            <Icon icon="gridicons:external" className="h-4 w-4" />

                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {/* Status Message for not-ready */}
            {!isCheckingStatus && (chatStatus === 'not-ready' || !chatStatus) && (
                <div className="flex flex-col items-center justify-center h-full -mt-36">
                    <div className="text-center space-y-4 max-w-md">
                        <Icon icon="ph:clock" className="h-12 w-12 mx-auto text-muted-foreground" />
                        <div>
                            <TypographySmall className="text-lg font-semibold mb-2">
                                Chat is not ready yet
                            </TypographySmall>
                            <TypographySmall className="text-muted-foreground">
                                Please check back soon. We're setting things up for you.
                            </TypographySmall>
                        </div>
                    </div>
                </div>
            )}

            {/* Loader when checking status or when preparing */}
            {(isCheckingStatus || chatStatus === 'preparing') && (
                <div className="flex flex-col items-center justify-center h-full -mt-20">
                    <div className="text-center space-y-12 max-w-md px-4">
                        <FutureLoader />
                        <div className="space-y-2">
                            {chatStatus === 'preparing' ? (
                                <>
                                    <TypographySmall className="text-base font-medium">
                                        <Shimmer duration={2.5}>
                                            We're analyzing your data
                                        </Shimmer>
                                    </TypographySmall>
                                    <TypographySmall className="text-sm text-muted-foreground">
                                        It will be ready soon. Check again in a few moments
                                    </TypographySmall>
                                </>
                            ) : (
                                <TypographySmall className="text-muted-foreground">
                                    Checking chat status...
                                </TypographySmall>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Conversation Area - Only show when status allows (idle or missing-context) */}
            {!isCheckingStatus && chatStatus !== 'not-ready' && chatStatus !== 'preparing' && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="flex-1 flex flex-col min-h-0 overflow-hidden"
                >
                    <Conversation className="flex-1 relative">
                        {/* <Lottie
                    style={{ width: "100%", position: "absolute", rotate: "180deg", top: "0", left: "0", zIndex: "1", display: isSafari() ? 'none' : 'block' }}
                    animationData={bottomAnimation}
                /> */}
                        <ConversationContent className="max-w-5xl mx-auto">
                            {messages.length === 0 ? (
                                <>
                                    <ConversationEmptyState
                                        className="z-10"
                                        icon={
                                            <div className="relative">
                                                <Icon
                                                    icon="ph:chat-circle-fill"
                                                    className="h-12 w-12 text-primary/80"
                                                />
                                                <motion.div
                                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                                                    animate={{
                                                        x: ['-70%', '70%']
                                                    }}
                                                    transition={{
                                                        duration: 2,
                                                        repeat: Infinity,
                                                        ease: "easeInOut",
                                                        repeatDelay: 1
                                                    }}
                                                    style={{
                                                        maskImage: 'radial-gradient(circle, black, transparent)',
                                                        WebkitMaskImage: 'radial-gradient(circle, black, transparent)'
                                                    }}
                                                />
                                            </div>
                                        }
                                        title="Ready when you are"
                                        description="Ask me anything about your creatives"
                                    />
                                    <motion.div
                                        className="py-4 w-full"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: 0.2 }}
                                    >
                                        <TypographySmall className="text-muted-foreground mb-3 font-medium text-center">
                                            Try asking:
                                        </TypographySmall>
                                        <Suggestions>
                                            {
                                                randomSuggestions.map((suggestion, index) => (
                                                    <Suggestion
                                                        key={index}
                                                        suggestion={suggestion}
                                                        onClick={handleSuggestionClick}
                                                        className="relative overflow-hidden"
                                                    >
                                                        <motion.div
                                                            className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/30 to-transparent z-10"
                                                            initial={{ x: '-100%' }}
                                                            animate={{ x: '100%' }}
                                                            transition={{ duration: 1.2, delay: index * 0.2, ease: 'easeInOut' }}
                                                        />
                                                        <TypographySmall>{suggestion}</TypographySmall>
                                                    </Suggestion>
                                                ))
                                            }

                                        </Suggestions>
                                    </motion.div>
                                </>
                            ) : (
                                <AnimatePresence mode="popLayout">
                                    {messages.map((message, index) => (
                                        <motion.div
                                            key={message.id}
                                            initial={{
                                                opacity: 0,
                                                y: 20,
                                                scale: 0.95
                                            }}
                                            animate={{
                                                opacity: 1,
                                                y: 0,
                                                scale: 1
                                            }}
                                            exit={{
                                                opacity: 0,
                                                scale: 0.95
                                            }}
                                            transition={{
                                                duration: 0.3,
                                                ease: [0.4, 0, 0.2, 1]
                                            }}
                                        >
                                            <Message from={message.role}>
                                                <MessageContent>
                                                    {message.role === 'assistant' ? (
                                                        <div className="space-y-2">
                                                            <AssistantMessage
                                                                message={message}
                                                                setCompletedMessages={setCompletedMessages}
                                                                completedMessages={completedMessages}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <motion.div
                                                            className="space-y-2"
                                                            initial={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            transition={{ delay: 0.1, duration: 0.2 }}
                                                        >
                                                            {message.autoSuggested && (
                                                                <Badge variant="outline" className="text-[10px] uppercase tracking-wide border border-gray-500 mr-1">
                                                                    Suggested question:
                                                                </Badge>
                                                            )}
                                                            <span className="text-sm">{message.content}</span>
                                                            {message.attachments && message.attachments.length > 0 && (
                                                                <div className="flex flex-wrap gap-2">
                                                                    {message.attachments.map((attachment, idx) => (
                                                                        <motion.div
                                                                            key={idx}
                                                                            className="relative"
                                                                            initial={{ opacity: 0, scale: 0.8 }}
                                                                            animate={{ opacity: 1, scale: 1 }}
                                                                            transition={{ delay: 0.1 + (idx * 0.05), duration: 0.2 }}
                                                                        >
                                                                            {attachment.type === 'file' && attachment.mediaType?.startsWith('image/') ? (
                                                                                <img
                                                                                    src={attachment.url}
                                                                                    alt={attachment.filename || 'attachment'}
                                                                                    className="max-w-48 rounded-lg object-cover"
                                                                                />
                                                                            ) : (
                                                                                <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg mb-2">
                                                                                    <Icon icon="ph:paperclip" className="h-4 w-4" />
                                                                                    <span className="text-xs">{attachment.filename || 'File'}</span>
                                                                                </div>
                                                                            )}
                                                                        </motion.div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </motion.div>
                                                    )}
                                                </MessageContent>
                                            </Message>
                                        </motion.div>
                                    ))}

                                    {isLoading && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <Message from="assistant">
                                                <MessageContent>
                                                    <Reasoning isStreaming={true} defaultOpen={true}>
                                                        <ReasoningTrigger />
                                                        {/* <ReasoningContent> */}
                                                        <div className="text-sm text-muted-foreground h-4 mt-2">

                                                            <TypewriterText text={REASONING_MESSAGES[currentReasoningIndex]} speed={20} />

                                                        </div>
                                                        {/* </ReasoningContent> */}
                                                    </Reasoning>

                                                    {externalClearChat && (
                                                        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 p-2.5 rounded-lg border border-dashed border-muted-foreground/20">
                                                            <Icon icon="lucide:info" className="w-4 h-4 text-sky-500 flex-shrink-0" />
                                                            <p>
                                                                You can close this chat - we’ll notify you when it’s ready
                                                            </p>
                                                        </div>
                                                    )}
                                                </MessageContent>
                                            </Message>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            )}
                        </ConversationContent>
                    </Conversation>
                    <div className="border-t p-4">
                        <div className="max-w-5xl mx-auto">
                            <div className={cn(
                                'relative transition-all duration-200',
                                selectedInstructionId && [
                                    '[&_[data-slot=input-group]]:ring-primary/40',
                                    '[&_[data-slot=input-group]]:border-primary/50',
                                    '[&_[data-slot=input-group]:has([data-slot=input-group-control]:focus-visible)]:ring-primary/60',
                                    '[&_[data-slot=input-group]:has([data-slot=input-group-control]:focus-visible)]:border-primary/60',
                                ].join(' ')
                            )}>
                                <AnimatePresence>
                                    {selectedInstructionId && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 4, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 4, scale: 0.95 }}
                                            transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                                            className="absolute -top-3 left-3 z-10 flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 backdrop-blur-sm px-2 py-0.5 shadow-sm"
                                        >
                                            <Icon icon="ph:list-checks" className="h-3 w-3 text-primary" />
                                            <span className="text-[11px] font-medium text-primary max-w-[180px] truncate">
                                                {instructions.find(i => (i.id || i._id) === selectedInstructionId)?.name || 'Instruction'}
                                            </span>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                                <PromptInput
                                    onSubmit={handleSubmit}
                                    className="w-full bg-background"
                                    accept="image/*,video/*,.pdf,.txt,.csv"
                                    multiple
                                >
                                    <PromptInputAttachments className="mr-auto">
                                        {(file) => <PromptInputAttachment key={file.id} data={file} />}
                                    </PromptInputAttachments>
                                    <PromptInputTextarea
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        placeholder={selectedInstructionId
                                            ? `Ask with "${instructions.find(i => (i.id || i._id) === selectedInstructionId)?.name || 'instruction'}"...`
                                            : 'Ask me anything...'
                                        }
                                        disabled={isLoading || isTyping || isChatCreditBlocked || !chatStatus || chatStatus === 'not-ready' || chatStatus === 'preparing'}
                                    />
                                    <PromptInputFooter>
                                        <PromptInputTools>
                                            <PromptInputButton
                                                onClick={() => {
                                                    document.querySelector('input[type="file"]')?.click()
                                                    AnalyticsService.track('chat-upload-button-clicked')
                                                }}
                                                aria-label="Upload files"
                                            >
                                                <Icon icon="ph:paperclip" className="h-4 w-4" />
                                            </PromptInputButton>
                                            <div className="relative" ref={instructionMenuRef}>
                                                <TooltipProvider delayDuration={0}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                type="button"
                                                                aria-label="Select instruction"
                                                                disabled={!isFirstMessage || isLoading}
                                                                onClick={() => {
                                                                    if (isFirstMessage && !isLoading) {
                                                                        setIsInstructionMenuOpen(prev => !prev)
                                                                        if (!isInstructionMenuOpen) {
                                                                            AnalyticsService.track('chat-instruction-menu-opened')
                                                                        }
                                                                    }
                                                                }}
                                                                className={cn(
                                                                    'inline-flex items-center gap-1.5 rounded-md px-2 h-8 text-sm transition-colors',
                                                                    isFirstMessage && !isLoading
                                                                        ? 'text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-pointer'
                                                                        : 'text-muted-foreground/50 cursor-not-allowed'
                                                                )}
                                                            >
                                                                <Icon icon="ph:list-checks" className="h-4 w-4 flex-shrink-0" />
                                                                {selectedInstructionId ? (
                                                                    <span className={cn(
                                                                        'max-w-[120px] truncate text-xs font-medium',
                                                                        isFirstMessage && !isLoading ? 'text-foreground' : 'text-muted-foreground/50'
                                                                    )}>
                                                                        {instructions.find(i => (i.id || i._id) === selectedInstructionId)?.name || 'Instruction'}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-xs">Instruction</span>
                                                                )}
                                                            </button>
                                                        </TooltipTrigger>
                                                        {(!isFirstMessage || isLoading) && (
                                                            <TooltipContent side="top" className="max-w-[220px] text-center">
                                                                <p>Instruction can't be changed mid-conversation. Clear the chat to start a new one.</p>
                                                            </TooltipContent>
                                                        )}
                                                    </Tooltip>
                                                </TooltipProvider>
                                                <AnimatePresence>
                                                    {isInstructionMenuOpen && isFirstMessage && !isLoading && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: 8, scale: 0.96 }}
                                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                                            exit={{ opacity: 0, y: 8, scale: 0.96 }}
                                                            transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                                                            className="absolute bottom-full left-0 mb-2 w-64 rounded-md border bg-popover p-1 text-popover-foreground shadow-md z-50"
                                                        >
                                                            <div className="max-h-60 overflow-y-auto">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setSelectedInstructionId(null)
                                                                        setIsInstructionMenuOpen(false)
                                                                    }}
                                                                    className="flex items-center gap-2 w-full rounded-md px-2 py-2 text-sm hover:bg-accent transition-colors"
                                                                >
                                                                    <span className="w-4 flex-shrink-0">
                                                                        {!selectedInstructionId && (
                                                                            <Icon icon="ph:check" className="h-4 w-4 text-primary" />
                                                                        )}
                                                                    </span>
                                                                    <span className="text-muted-foreground">None</span>
                                                                </button>
                                                                {instructions.map(inst => {
                                                                    const instId = inst.id || inst._id
                                                                    return (
                                                                        <button
                                                                            type="button"
                                                                            key={instId}
                                                                            onClick={() => {
                                                                                setSelectedInstructionId(instId)
                                                                                setIsInstructionMenuOpen(false)
                                                                                AnalyticsService.track('chat-instruction-selected', { instructionId: instId })
                                                                            }}
                                                                            className="flex items-start gap-2 w-full rounded-md px-2 py-2 text-sm hover:bg-accent transition-colors text-left"
                                                                        >
                                                                            <span className="w-4 flex-shrink-0 mt-0.5">
                                                                                {selectedInstructionId === instId && (
                                                                                    <Icon icon="ph:check" className="h-4 w-4 text-primary" />
                                                                                )}
                                                                            </span>
                                                                            <div className="min-w-0 flex-1">
                                                                                <div className="font-medium truncate">{inst.name}</div>
                                                                                {inst.description && (
                                                                                    <div className="text-xs text-muted-foreground truncate">
                                                                                        {inst.description}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </button>
                                                                    )
                                                                })}
                                                            </div>
                                                            <div className="border-t mt-1 pt-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setIsInstructionMenuOpen(false)
                                                                        closeDrawer()
                                                                        navigate(`/account/${accountId}/space/${spaceId}/dashboard/settings/space/ai-instructions`)
                                                                    }}
                                                                    className="flex items-center gap-2 w-full rounded-md px-2 py-2 text-sm hover:bg-accent transition-colors text-muted-foreground"
                                                                >
                                                                    <Icon icon="ph:plus" className="h-4 w-4" />
                                                                    <span>Create new instruction</span>
                                                                    <Icon icon="gridicons:external" className="h-3 w-3 ml-auto" />
                                                                </button>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </PromptInputTools>
                                        <PromptInputSubmit
                                            disabled={isLoading || isTyping || isChatCreditBlocked || !chatStatus || chatStatus === 'not-ready' || chatStatus === 'preparing'}
                                            status={isLoading ? 'submitted' : undefined}
                                        />
                                    </PromptInputFooter>
                                </PromptInput>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </div>
    )
}
